import React, { useRef } from 'react';
import { IconButton } from '@cbrebuild/blocks';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import considerationsService from '../../../services/voucher/considerations-service';
import { selectAdditionalCommissionAmountTotal, selectConsiderationsCommissionAmountTotal } from '../../../redux/selectors/voucher-selectors';
import Currency from '../../../nucleus/formats/currency';
import formatAsCurrency from '../../../utils/format-as-currency';
import { getTotalConsiderationTermMonths, sortConsiderationsByPositionAndId } from '../../../utils/consideration-term-utils';
import { useRegisterRequiredRows, useRegisterRequiredField, useRegisterAddRowAction } from './RequiredFieldsContext';

const selectTotalTerms = state => getTotalConsiderationTermMonths(state.voucherConsiderations.considerations);
const selectTotalConsiderations = state => state.voucherConsiderations.considerations.reduce((acc, cur) => acc + (cur.consideration_amount || 0), 0);

const ConsiderationReview = () => {
  const {
    additionalCommissions, considerations, deal, voucher,
  } = useSelector(state => ({
    additionalCommissions: state.voucherAdditionalCommissions?.additionalCommissions,
    considerations: state.voucherConsiderations?.considerations,
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
  }));

  const totalTerms = useSelector(selectTotalTerms, shallowEqual);
  const totalConsiderations = useSelector(selectTotalConsiderations, shallowEqual);
  const totalCommissions = useSelector(selectConsiderationsCommissionAmountTotal, shallowEqual);
  const totalAddCommissions = useSelector(selectAdditionalCommissionAmountTotal, shallowEqual);
  const history = useHistory();

  const requiredText = () => {
    localStorage.setItem('voucher_valid', JSON.stringify(false));
    return <span className="voucher-error-text">Required</span>;
  };
  const dealType = deal.deal_type;
  const isLeaseOrSaleDealType = dealType === 'sale' || dealType === 'lease';
  const isFreeRent = (type) => {
    if (considerationsService.FREE_LEASE.indexOf(type) > -1) {
      return true;
    }
    return false;
  };

  const strings = {
    title: 'Consideration',
    url: VOUCHER_ROUTES[2].routes[0].path,
    unitLabel: 'Period',
    amountLabel: 'Rent',
    additionalCommissionsTitle: 'Additional Commission',
  };
  switch (dealType) {
    case 'sale':
      strings.url = VOUCHER_ROUTES[2].routes[0].path;
      strings.unitLabel = 'Quantity';
      strings.amountLabel = 'Price';
      break;
    case 'consulting':
    case 'other':
      strings.title = 'Commissions';
      strings.url = VOUCHER_ROUTES[2].routes[2].path;
      strings.additionalCommissionsTitle = 'Commissions';
      break;
    default:
  }
  strings.url = strings.url.replace(':id', voucher.deal);

  const disabledTypeMap = {
    'Free Monthly Rent': true,
    'Free Annual Rent': true,
  };
  const sortedConsiderations = sortConsiderationsByPositionAndId(considerations);

  // Shared by the row registration below and the "at least one consideration" checks
  // further down, so both agree on what counts as a fully filled-out consideration.
  const isConsiderationMissing = consideration => !consideration.consideration_type || !consideration.total_units
    || (!disabledTypeMap[consideration.consideration_type] && !consideration.amount);

  // Register each incomplete row (any single missing field) into the "Before you submit"
  // panel, showing the ENTIRE row there using the exact same editable row component/UX/redux
  // dispatch behavior as this live Financials tab -- mirrors the requiredText() checks above.
  useRegisterRequiredRows(
    isLeaseOrSaleDealType ? sortedConsiderations : [],
    {
      getFieldId: consideration => `consideration-${consideration.id}`,
      isMissing: isConsiderationMissing,
      buildEntry: consideration => ({
        section: strings.title,
        label: consideration.consideration_type || 'Consideration',
        customRowType: 'consideration',
        rowGroup: 'consideration',
        rowId: consideration.id,
        // Not spread onto <ConsiderationItem> (it gets deal_type from its own useSelector) --
        // only used by RequiredFieldsSummary's header row to match Considerations.js's
        // "Period"/"Rent($)" (lease) vs "Quantity"/"Price($)" (sale) column labels exactly.
        rowProps: { unitLabel: strings.unitLabel, amountLabel: strings.amountLabel },
      }),
    },
    [sortedConsiderations, isLeaseOrSaleDealType, strings.title, strings.unitLabel, strings.amountLabel],
  );

  useRegisterRequiredRows(
    additionalCommissions,
    {
      getFieldId: additionalCommission => `additional-commission-${additionalCommission.id}`,
      isMissing: additionalCommission => !additionalCommission.acommission_type
        || !additionalCommission.total_units || !additionalCommission.amount,
      buildEntry: additionalCommission => ({
        section: strings.additionalCommissionsTitle,
        label: additionalCommission.acommission_type || 'Additional Commission',
        customRowType: 'additionalCommission',
        rowId: additionalCommission.id,
      }),
    },
    [additionalCommissions, strings.additionalCommissionsTitle],
  );

  // Registers the "You must add at least one consideration" message into the "Before you
  // submit" panel until at least one consideration row is FULLY filled out -- a row that
  // already exists (however it was created, e.g. from elsewhere in the voucher flow) but is
  // still missing required fields does not satisfy this, matching the row itself still
  // showing up as incomplete above.
  const considerationListMissing = isLeaseOrSaleDealType && !considerations.some(consideration => !isConsiderationMissing(consideration));
  useRegisterRequiredField(
    'consideration-list-required',
    {
      isReview: true,
      missing: considerationListMissing,
      buildEntry: () => ({
        section: strings.title,
        label: strings.title,
        requiredMessage: `You must add at least one consideration to this ${voucher.is_early_invoice ? 'early invoice' : 'voucher'}`,
        // Once any consideration row exists (even incomplete), ITS OWN missing-row entry
        // (registered above via useRegisterRequiredRows) already represents this same gap in
        // the "N required fields" footer count -- stop double-counting this message too
        // (it still keeps showing/staying red until a row is complete, just excluded here).
        excludeFromCount: considerations.length > 0,
      }),
    },
    [considerationListMissing, strings.title, voucher.is_early_invoice, considerations.length],
  );

  // Registers the "Add Consideration" button into the panel -- reusing the exact same
  // `createConsideration` add button the live Financials tab renders. Freezes whether the
  // requirement was outstanding at the moment this component first mounted, so a user
  // adding several considerations in one sitting keeps seeing the button, but switching
  // tabs/screens (a fresh mount) re-evaluates it and hides the button (and the whole
  // Consideration section, once it has no other registered entries left) if a consideration
  // already exists by then -- positioned directly below the consideration rows via
  // `rowGroup: 'consideration'` + `isAddButton` (see `orderSectionEntries` in
  // `RequiredFieldsSummary.js`) while it is shown.
  const initialConsiderationListMissingRef = useRef(null);
  if (initialConsiderationListMissingRef.current === null) {
    initialConsiderationListMissingRef.current = considerationListMissing;
  }
  useRegisterAddRowAction(
    'consideration-add-button',
    initialConsiderationListMissingRef.current,
    () => ({
      section: strings.title,
      customRowType: 'considerationAdd',
      rowGroup: 'consideration',
      isAddButton: true,
      rowProps: { dealId: deal.id, dealType },
    }),
    [strings.title, deal.id, dealType, initialConsiderationListMissingRef.current],
  );

  return (
    <div className="voucher-review-group consideration-review">
      <div className="navigation">
        <Link className="h2 link" to={strings.url}>{strings.title}</Link>
        <IconButton iconName="edit" onClick={() => { history.push(strings.url); }} />
      </div>
      <div className="content">
        {isLeaseOrSaleDealType && <h3>Consideration Breakdown</h3>}
        {!considerations.length && isLeaseOrSaleDealType && (() => {
          localStorage.setItem('voucher_valid', JSON.stringify(false));
          return (
            <div className="voucher-error-text error">
              You must add at least one consideration to this {voucher.is_early_invoice ? 'early invoice' : 'voucher'}
            </div>
          );
        })()}
        {isLeaseOrSaleDealType &&
          <table>
            <colgroup>
              <col name="consideration type" />
              <col name="unit label" />
              <col name="amount label" />
              <col name="consideration" />
              <col name="commission rate" />
              <col name="gross commissions" />
            </colgroup>
            <tbody>
              <tr>
                <th>Consideration Type</th>
                <th>{strings.unitLabel}</th>
                <th>{strings.amountLabel}</th>
                <th>Consideration</th>
                <th>Commission Rate</th>
                <th>Gross Commissions</th>
              </tr>
              {sortedConsiderations.map(consideration => (
                <tr key={consideration.id}>
                  <td>{consideration.consideration_type || requiredText()}</td>
                  <td>
                    {consideration.total_units || requiredText()}
                  </td>
                  {(!!disabledTypeMap[consideration.consideration_type] && <td><Currency value={consideration.amount} emptyState="$0.00" /></td>)
                    || (
                      <td>{consideration.amount ? <Currency value={consideration.amount} emptyState="" />
                        : requiredText()}
                      </td>)}
                  <td><Currency value={consideration.consideration_amount} emptyState="$0.00" /></td>
                  {!isFreeRent(consideration.consideration_type) && <td>{consideration.commission_percent}</td>}
                  {isFreeRent(consideration.consideration_type) && <td>N/A</td>}
                  <td><Currency value={consideration.commission_amount} emptyState="$0.00" /></td>
                </tr>
            ))}
              <tr>
                <td>Totals</td>
                <td>
                  {dealType !== 'lease' ? '' : totalTerms} Months
                </td>
                <td />
                <td><Currency value={totalConsiderations} emptyState="$0.00" /></td>
                <td />
                <td><Currency value={totalCommissions} emptyState="$0.00" /></td>
                <td />
              </tr>
            </tbody>
          </table>
        }
        <h3>{strings.additionalCommissionsTitle}</h3>
        <table>
          <colgroup>
            <col name="commission type" />
            <col name="quantity" />
            <col name="amount" />
            <col name="additional commission" />
          </colgroup>
          <tbody>
            <tr>
              <th>Commission Type</th>
              <th>Quantity</th>
              <th>Amount($)</th>
              <th>Additional Commission</th>
              <th />
            </tr>
            {additionalCommissions.map(additionalCommission => (
              <tr key={additionalCommission.id}>
                <td>{additionalCommission.acommission_type || requiredText()}</td>
                <td>{additionalCommission.total_units || requiredText()}</td>
                <td>{formatAsCurrency(additionalCommission.amount) || requiredText()}</td>
                <td>{formatAsCurrency(additionalCommission.commission_amount) || requiredText()}</td>
              </tr>
            ))}
            <tr>
              <td>Totals</td>
              <td />
              <td />
              <td><Currency value={totalAddCommissions} emptyState="$0.00" /></td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsiderationReview;
